import { useEffect, useMemo } from 'react'

import { useElara } from '@/providers/elara/hooks/useElara'
import { useElaraActions } from '@/providers/elara/hooks/useElaraActions'
import type { UIAction } from '@/providers/elara/types'

const SIDEBAR_SECTIONS = [
  'patientInfo', 'mesures', 'forms', 'transcriptions', 'documents', 'payment', 'medicalActs',
] as const

const DIAGNOSTIC_TABS = ['mesures', 'medicalVideo', 'stethoscope', 'biologicals'] as const

const ALL_SECTIONS = [...SIDEBAR_SECTIONS, 'medicalVideo', 'stethoscope', 'biologicals'] as const

type ElaraNavigationActionsProps = {
  selectedMenuItem: string
  diagnosticSubTab: string | null
  onSelectMenuItem: (id: string) => void
  onSetDiagnosticSubTab: (tab: string | null) => void
  onOpenDocuments: () => void
  onCloseDocuments: () => void
  onShowQrCode: () => void
  consultationId: string
}

export function ElaraNavigationActions({
  selectedMenuItem,
  diagnosticSubTab,
  onSelectMenuItem,
  onSetDiagnosticSubTab,
  onOpenDocuments,
  onCloseDocuments,
  onShowQrCode,
  consultationId,
}: ElaraNavigationActionsProps) {
  const currentLocation = selectedMenuItem === 'mesures' && diagnosticSubTab
    ? `mesures/${diagnosticSubTab}`
    : selectedMenuItem

  const { updateUIState } = useElara()!
  useEffect(() => {
    updateUIState('general', { currentSection: currentLocation })
  }, [currentLocation, updateUIState])

  const elaraActions = useMemo((): UIAction[] => [
    {
      actionId: 'nav.switchSection',
      description: `Switch the active section/tab. Available sections: ${ALL_SECTIONS.join(', ')}. ` +
        `The "mesures" section is the All Diagnostics view containing sub-tabs: mesures (vital signs — tension, saturation, temperature, etc.), medicalVideo, stethoscope, biologicals. ` +
        `To go to vital signs / measurements, use section="mesures" (it opens the vital signs tab by default). ` +
        `To go to stethoscope/medicalVideo/biologicals, use the sub-tab name directly. ` +
        `Current location: "${currentLocation}".`,
      section: 'general',
      params: [
        {
          name: 'section',
          type: 'enum' as const,
          description: 'The section ID to navigate to',
          required: true,
          enumValues: [...ALL_SECTIONS],
        },
      ],
      handler: async (params) => {
        if (!params?.section) return { success: false, message: 'No section provided' }

        // Close documents dialog when navigating away
        onCloseDocuments()

        const isDiagnosticTab = (DIAGNOSTIC_TABS as readonly string[]).includes(params.section)

        if (isDiagnosticTab) {
          onSelectMenuItem('mesures')
          onSetDiagnosticSubTab(params.section)
          return { success: true, message: `Switched to ${params.section === 'mesures' ? 'vital signs' : params.section}` }
        }

        onSetDiagnosticSubTab(null)
        onSelectMenuItem(params.section)
        return { success: true, message: `Switched to ${params.section}` }
      },
    },
    {
      actionId: 'nav.getCurrentSection',
      description: `Get the currently active section. Returns the section ID and diagnostic sub-tab if any.`,
      section: 'general',
      params: [],
      handler: async () => {
        return { success: true, message: currentLocation, data: { section: selectedMenuItem, diagnosticSubTab } }
      },
    },
    {
      actionId: 'documents.open',
      description: 'Open the documents dialog to view, upload, or manage consultation files.',
      section: 'general',
      params: [],
      handler: async () => {
        onOpenDocuments()
        return { success: true, message: 'Documents dialog opened' }
      },
    },
    {
      actionId: 'documents.showUploadQR',
      description: 'Show the patient file upload QR code. The patient can scan this to upload documents (lab results, photos) directly to the consultation.',
      section: 'general',
      params: [],
      handler: async () => {
        onShowQrCode()
        return { success: true, message: 'QR code displayed for patient file uploads' }
      },
    },
  ], [currentLocation, selectedMenuItem, diagnosticSubTab, onSelectMenuItem, onSetDiagnosticSubTab, onOpenDocuments, onCloseDocuments, onShowQrCode, consultationId])

  useElaraActions(elaraActions)

  return null
}
